/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Custom destinations", description = "Send AI Decisioning decisions to any destination in the Hightouch catalog, including a raw HTTP endpoint that posts to your own API.", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Custom destinations",
        description: "Send AI Decisioning decisions to any destination in the Hightouch catalog, including a raw HTTP endpoint that posts to your own API.",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How custom channels differ from managed channels",
          "depth": 1,
          "id": "how-custom-channels-differ-from-managed-channels"
        }, {
          "value": "1. Connect your destination",
          "depth": 0,
          "id": "1-connect-your-destination"
        }, {
          "value": "2. Add a custom channel",
          "depth": 0,
          "id": "2-add-a-custom-channel"
        }, {
          "value": "3. Add messages",
          "depth": 0,
          "id": "3-add-messages"
        }, {
          "value": "4. Build the delivery sync",
          "depth": 0,
          "id": "4-build-the-delivery-sync"
        }, {
          "value": "Available columns",
          "depth": 1,
          "id": "available-columns"
        }, {
          "value": "Map your message content",
          "depth": 1,
          "id": "map-your-message-content"
        }, {
          "value": "5. Schedule and validate",
          "depth": 0,
          "id": "5-schedule-and-validate"
        }, {
          "value": "Manage messages with the API",
          "depth": 0,
          "id": "manage-messages-with-the-api"
        }, {
          "value": "Troubleshooting",
          "depth": 0,
          "id": "troubleshooting"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      ol: "ol",
      img: "img",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Technical marketers, data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/ai-decisioning/prepare-data",
                    children: "Prepare data for AID"
                  })
                }), _jsxs("li", {
                  children: ["A destination connected in Hightouch (such as ", _jsx(_components.a, {
                    href: "/destinations/http-request",
                    children: "HTTP Request"
                  }), ")"]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "A custom channel sends an agent's decisions to any destination in the Hightouch catalog — including a raw HTTP endpoint that posts each decision to your own API."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["AI Decisioning delivers decisions through your connected Hightouch destinations. It has purpose-built integrations for ", _jsx(_components.a, {
          href: "/ai-decisioning/braze",
          children: "Braze"
        }), ", ", _jsx(_components.a, {
          href: "/ai-decisioning/iterable",
          children: "Iterable"
        }), ", and ", _jsx(_components.a, {
          href: "/ai-decisioning/sfmc",
          children: "Salesforce Marketing Cloud"
        }), " that manage message delivery for you."]
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "custom channel"
        }), " extends AI Decisioning to any other destination in the Hightouch catalog. If you can sync to a destination today, AI Decisioning can deliver to it: you connect the destination, and AI Decisioning writes its decisions to a model you sync from. The most common use is a raw HTTP endpoint, where AI Decisioning posts each decision to your own API using the ", _jsx(_components.a, {
          href: "/destinations/http-request",
          children: "HTTP Request destination"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Use a custom channel when:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Your destination doesn't have a purpose-built AI Decisioning integration."
        }), "\n", _jsx(_components.li, {
          children: "You deliver through middleware you already own, such as a Lambda or an internal service."
        }), "\n", _jsx(_components.li, {
          children: "You want to write decisions to a warehouse, object store, or other system for downstream processing."
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you'd like a purpose-built integration for a destination you use, and\nHightouch can already sync to it, we can usually add AI Decisioning support.", " ", "\n", _jsx(PylonLink, {
            pylonText: "Hi! I'd like to request a purpose-built AI Decisioning integration for a destination we sync to. Destination: [name].",
            linkText: "Contact your Hightouch team"
          }), " ", "\nto request one."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "how-custom-channels-differ-from-managed-channels",
        children: "How custom channels differ from managed channels"
      }), "\n", _jsx(_components.p, {
        children: "With a managed channel (Braze, Iterable, or Salesforce Marketing Cloud), Hightouch creates and manages the delivery sync for you, and message content is sourced from a template in your destination. A custom channel works differently in two ways:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "You build and own the delivery sync."
          }), " AI Decisioning writes each agent's decisions to a Hightouch model. You create a normal sync from that model to your destination, and you own its schedule and monitoring."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Message content lives in Hightouch."
          }), " You author each message's content as variables in AI Decisioning. There's no destination template to select."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This gives you full control over the request Hightouch sends, at the cost of managing the sync yourself."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "1-connect-your-destination",
        children: "1. Connect your destination"
      }), "\n", _jsxs(_components.p, {
        children: ["Connect the destination you want AI Decisioning to send to. For a raw HTTP endpoint, add the ", _jsx(_components.a, {
          href: "/destinations/http-request",
          children: "HTTP Request destination"
        }), ", where you configure the base URL, authentication, and headers for your API."]
      }), "\n", _jsx(_components.p, {
        children: "Store API keys and tokens in the destination's authentication or headers, not in the request body."
      }), "\n", _jsxs(_components.p, {
        children: ["You'll configure the request method and payload later, when you ", _jsx(_components.a, {
          href: "#4-build-the-delivery-sync",
          children: "build the delivery sync"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "2-add-a-custom-channel",
        children: "2. Add a custom channel"
      }), "\n", _jsx(_components.p, {
        children: "A channel connects an agent's decisions to a destination."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to ", _jsx(_components.strong, {
              children: "AI Decisioning > Configuration > Channels"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Add channel"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["For the channel type, select ", _jsx(_components.strong, {
              children: "Custom"
            }), " (\"Send personalized decisions to any destination in our catalog\")."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "/ai-decisioning/custom-destinations/aid-custom-select-channel.png",
              alt: "New channel dropdown showing the Custom channel type"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Select your destination."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select an ", _jsx(_components.strong, {
              children: "identifier"
            }), " — the user column that identifies the recipient, such as ", _jsx(_components.code, {
              children: "email"
            }), " or ", _jsx(_components.code, {
              children: "user_id"
            }), ". This becomes the ", _jsx(_components.code, {
              children: "external_id"
            }), " column in the model AI Decisioning writes to."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Add channel"
            }), ", then select the channel and select ", _jsx(_components.strong, {
              children: "Save changes"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A configured custom channel shows the destination, identifier, eligibility filters, and tags:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/custom-destinations/aid-custom-channel-configured.png",
          alt: "A configured custom channel sending to HubSpot, with an identifier, eligibility filters, and tags"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Custom channels don't apply a channel tag automatically. To enable frequency\nlimits, add a channel name tag to the channel. See\n", _jsx(_components.a, {
            href: "/ai-decisioning/tags",
            children: "Tags"
          }), "."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "3-add-messages",
        children: "3. Add messages"
      }), "\n", _jsx(_components.p, {
        children: "Each message defines the content an agent can send. For a custom channel, you author the content directly in Hightouch as variables."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "AI Decisioning > Agents"
          }), " and select your agent."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Messages"
          }), " tab."]
        }), "\n", _jsx(_components.li, {
          children: "Add a message and select your custom channel and destination."
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab, define the message's variables (for example, ", _jsx(_components.code, {
            children: "subject"
          }), " and ", _jsx(_components.code, {
            children: "body"
          }), ") and add variants."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Each variable you define appears in the delivery sync inside the ", _jsx(_components.code, {
          children: "hightouch_recommendation"
        }), " column. For full details on messages, variants, and tags, see ", _jsx(_components.a, {
          href: "/ai-decisioning/messages",
          children: "Messages"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "4-build-the-delivery-sync",
        children: "4. Build the delivery sync"
      }), "\n", _jsx(_components.p, {
        children: "When you enable the first message on a custom channel, AI Decisioning creates a model that holds each agent's decisions. Build a sync from that model to your destination, mapping its columns into the request your destination sends."
      }), "\n", _jsx(_components.h3, {
        id: "available-columns",
        children: "Available columns"
      }), "\n", _jsx(_components.p, {
        children: "The model exposes a fixed set of columns:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "external_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The recipient identifier, taken from the identifier column you selected on the channel."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "A unique identifier for the decision."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The campaign identifier, used for ", _jsx(_components.a, {
                href: "/ai-decisioning/messages#campaign-attribution",
                children: "attribution"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "message_id"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The message the agent selected."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "send_at"
              })
            }), _jsx(_components.td, {
              children: "Timestamp"
            }), _jsx(_components.td, {
              children: "When the agent recommends sending."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "hightouch_recommendation"
              })
            }), _jsx(_components.td, {
              children: "JSON"
            }), _jsxs(_components.td, {
              children: ["The agent's decision, including a ", _jsx(_components.code, {
                children: "variables"
              }), " object with your message content."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "hightouch_user"
              })
            }), _jsx(_components.td, {
              children: "JSON"
            }), _jsx(_components.td, {
              children: "The recipient's synced warehouse columns."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "map-your-message-content",
        children: "Map your message content"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "hightouch_recommendation"
        }), " and ", _jsx(_components.code, {
          children: "hightouch_user"
        }), " are JSON columns. To read a value, parse the column and reference the field you want."]
      }), "\n", _jsx(_components.p, {
        children: "For the HTTP Request destination, build the request body to match what your API expects. This example reads the recipient identifier, the campaign, and two message variables:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "% assign parsedRecs = row", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "'hightouch_recommendation'", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), " | parse %", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"user_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"{{ row['external_id'] }}\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"campaign\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"{{ row['campaign_id'] }}\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"subject\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"{{ parsedRecs['variables']['subject'] }}\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"body\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"{{ parsedRecs['variables']['body'] }}\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Use the same pattern for any variable you defined on the message: ", _jsx(_components.code, {
          children: "parsedRecs['variables']['<variable name>']"
        }), ". To read a synced user column, parse ", _jsx(_components.code, {
          children: "hightouch_user"
        }), " the same way."]
      }), "\n", _jsxs(_components.p, {
        children: ["To hand your API the entire decision instead of individual fields, pass the ", _jsx(_components.code, {
          children: "hightouch_recommendation"
        }), " column through as a JSON object and transform it in your own service."]
      }), "\n", _jsxs(_components.p, {
        children: ["For the full set of request options — method, path, authentication, headers, rate limits, batching, and error handling — see the ", _jsx(_components.a, {
          href: "/destinations/http-request",
          children: "HTTP Request destination"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "5-schedule-and-validate",
        children: "5. Schedule and validate"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Set the sync schedule to match how often your agent makes decisions."
        }), "\n", _jsx(_components.li, {
          children: "Run the sync on a small test group first. Confirm your endpoint receives requests in the expected shape before enabling it broadly."
        }), "\n", _jsx(_components.li, {
          children: "Enable the sync."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A successful sync means your endpoint accepted the request. It doesn't confirm the message reached the user — check your destination's own delivery logs for that."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To compare agent performance against your existing campaigns, map a consistent\nvalue into ", _jsx(_components.code, {
            children: "campaign_id"
          }), " and use ", _jsx(_components.a, {
            href: "/ai-decisioning/messages#campaign-attribution",
            children: "campaign\nattribution"
          }), ". Attribution\nmatches on exact text, so ", _jsx(_components.code, {
            children: "Summer_Push"
          }), " and ", _jsx(_components.code, {
            children: "summer_push"
          }), " are treated as\ndifferent campaigns."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "manage-messages-with-the-api",
        children: "Manage messages with the API"
      }), "\n", _jsx(_components.p, {
        children: "You can create and update custom-channel messages, and trigger a run, with the Hightouch API. Use it to manage messages programmatically instead of in the app."
      }), "\n", _jsx(_components.p, {
        children: "The API manages messages and runs in an existing agent. Create the custom channel and its destination in the app first — the API doesn't create them."
      }), "\n", _jsx(_components.p, {
        children: "Create a message in an existing custom channel:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["curl -X POST ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://api.hightouch.com/api/v1/decision-engine/flow/{flowId}/messages'"
          }), " \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Authorization: Bearer YOUR_API_TOKEN'"
          }), " \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Content-Type: application/json'"
          }), " \\\n  -d ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'{\n    \"name\": \"Recommended offer\",\n    \"channelId\": \"YOUR_CHANNEL_ID\",\n    \"config\": { \"baseMessageId\": \"recommended-offer\" },\n    \"variables\": [\n      {\n        \"name\": \"subject\",\n        \"variants\": [{ \"value\": \"A gift for you\" }, { \"value\": \"Welcome back\" }]\n      }\n    ]\n  }'"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Trigger a run once the agent and its messages are enabled:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["curl -X POST ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://api.hightouch.com/api/v1/decision-engine/flow/{flowId}/run'"
          }), " \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Authorization: Bearer YOUR_API_TOKEN'"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A run evaluates eligibility and selects recommendations, then writes decisions to the model your sync reads from. It doesn't post directly to your endpoint. For the full API, see the ", _jsx(_components.a, {
          href: "/api-reference",
          children: "API reference"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Issue"
            }), _jsx(_components.th, {
              children: "Likely cause"
            }), _jsx(_components.th, {
              children: "Resolution"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Endpoint receives no requests"
            }), _jsx(_components.td, {
              children: "The channel, message, or sync isn't enabled"
            }), _jsx(_components.td, {
              children: "Confirm the channel and message are enabled and the sync is running. Check that eligible users have a non-null identifier."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Requests rejected with a validation error"
            }), _jsx(_components.td, {
              children: "The request body doesn't match your API's schema"
            }), _jsxs(_components.td, {
              children: ["Compare your body template to the shape your API expects. Confirm ", _jsx(_components.code, {
                children: "hightouch_recommendation"
              }), " is sent as a JSON object, not a string."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Requests accepted but nothing is delivered"
            }), _jsx(_components.td, {
              children: "A successful request only means your endpoint accepted it"
            }), _jsxs(_components.td, {
              children: ["Check your destination's own delivery logs. A ", _jsx(_components.code, {
                children: "2xx"
              }), " response doesn't confirm delivery to the user."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Duplicate requests"
            }), _jsx(_components.td, {
              children: "Retries or full resyncs replay rows"
            }), _jsx(_components.td, {
              children: "Enable retries only when your endpoint is idempotent. Use an idempotency key if your API supports one."
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/messages",
            children: "Messages"
          }), " to author the content and variants your custom channel delivers."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/destinations/http-request",
            children: "HTTP Request destination"
          }), " for the request options you configure on the delivery sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/qa-guide",
            children: "QA guide"
          }), " to validate messages before you launch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/api-reference",
            children: "Hightouch API reference"
          }), " to manage messages and trigger runs programmatically."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
